import { useCallback, useContext, useMemo, useState } from 'react'
import clsx from 'clsx'

import CornerControls from '../DetailItemCard/common/CornerControls'
import type { DetailItemCardVariant } from '../DetailItemCard/common/types'

import type { NoteItem } from 'tripPlanner/types/tripItem'
import TripItemInlineNoteEdit from '../ItemCard/TripItemInlineNoteEdit'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import ItemPin from '../CleanCard/common/ItemPin'
import { getItemPinCategory } from '../CleanCard/common/itemPinTypes'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import { getItemPinBadge } from 'tripPlanner/utils/tripItemPin'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'
import { CompactCardAndPinLayout, CompactCardLayout } from './common'
import styled from 'styled-components'
import { rem } from 'polished'
import HoverOnlyRegenerateItemButton from 'tripPlanner/components/CleanCard/common/HoverOnlyRegenerateItemButton'

const StyledTripItemInlineNoteEdit = styled(TripItemInlineNoteEdit)`
  /* This is for making note area in the same row with corner controls */
  width: calc(100% - ${rem(40)});

  &:hover,
  ${CompactCardAndPinLayout.CardContainer}.focus &,
  ${CompactCardAndPinLayout.CardContainer}:hover & {
    width: calc(100% - ${rem(80)});
  }
`

interface Props {
  item: NoteItem
  variant: DetailItemCardVariant
}

function CompactNoteCard({ item, variant }: Props) {
  const canEdit = useCanEditTrip()
  const [inFocus, setInFocus] = useState(false)

  const { data: trip } = useTrip({ tripId: item.tripId })

  const { itemNumber, itemColor } = useMemo(
    () => getItemPinBadge(item, variant, trip),
    [item, variant, trip],
  )

  const documentContext = useContext(TripDocumentsContext)
  const attachedDocuments =
    documentContext?.documentsResult?.attachedFiles[item.id] ?? []

  const hasNoAttachments = !!documentContext?.documentsResult && attachedDocuments.length === 0

  const onFocus = useCallback(() => {
    setInFocus(true)
  }, [])

  const onBlur = useCallback(() => {
    setInFocus(false)
  }, [])

  return (
    <CompactCardAndPinLayout>
      <CompactCardAndPinLayout.Pin className="pin-anchor">
        <ItemPin
          variant={variant}
          category={getItemPinCategory(item.type)}
          itemNumber={itemNumber}
          dayColor={itemColor}
        />
      </CompactCardAndPinLayout.Pin>
      <CompactCardAndPinLayout.CardContainer className={clsx({ focus: inFocus })}>
        <CompactCardLayout.Content>
          {canEdit && <>
            <HoverOnlyRegenerateItemButton item={item} />
            <CornerControls item={item} />
            <StyledTripItemInlineNoteEdit
              item={item}
              inputAlwaysVisible
              deleteItemOnClear={hasNoAttachments}
              inFocus={inFocus}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          </>}

          {!canEdit && <TripItemInlineNoteEdit item={item} inputAlwaysVisible deleteItemOnClear={hasNoAttachments} />}
        </CompactCardLayout.Content>
      </CompactCardAndPinLayout.CardContainer>
    </CompactCardAndPinLayout>
  )
}

export default CompactNoteCard
